import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { formsApi, type FormListItem } from '../api/forms'
import { departmentsApi, type Department } from '../api/team'
import {
  ButtonLink, IconButtonLink, Callout, DataTable, EmptyState, FilterBar, LoadingState, Page, PageHeader, Select, StatusPill,
} from '../components/ui'

export function FormsPage() {
  const [items, setItems] = useState<FormListItem[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void departmentsApi.list().then(setDepartments).catch(() => { /* filter dropdown only, not fatal */ })
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const depId = departmentFilter ? parseInt(departmentFilter, 10) : undefined
      const res = await formsApi.list(depId)
      setItems(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [departmentFilter])

  useEffect(() => { void load() }, [load])

  function departmentName(id: number | null): string | null {
    if (id == null) return null
    return departments.find(d => d.id === id)?.name ?? `#${id}`
  }

  return (
    <Page>
      <PageHeader
        title="Form"
        icon="dynamic_form"
        {...(items.length > 0 ? { count: items.length } : {})}
        actions={
          <ButtonLink to="/forms/create" variant="filled" icon="add">
            Nuovo form
          </ButtonLink>
        }
      />

      {/* Department filter — backend supports ?departmentId= on GET /api/forms */}
      <FilterBar>
        <Select
          label="Filtra per reparto"
          value={departmentFilter}
          onChange={e => setDepartmentFilter(e.target.value)}
        >
          <option value="">Tutti i reparti</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <EmptyState icon="dynamic_form" title="Nessun form" />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th className="ps-3">Nome</th>
              <th>Slug</th>
              <th>Reparto</th>
              <th>Stato</th>
              <th className="text-end pe-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map(f => (
              <tr key={f.id}>
                <td className="ps-3">
                  <Link to={`/forms/${f.id}`} className="text-decoration-none md-typescale-emphasized-body-medium">
                    {f.name}
                  </Link>
                </td>
                <td>
                  <code className="md-typescale-body-small">{f.slug}</code>
                </td>
                <td>
                  {departmentName(f.departmentId) ?? <span className="text-muted">—</span>}
                </td>
                <td>
                  {/* List only ever returns active forms (backend filters
                      Where(f => f.IsActive) before projecting, and doesn't project
                      isActive itself) — every row here is guaranteed active. */}
                  <StatusPill tone="success">Attivo</StatusPill>
                </td>
                <td className="text-end pe-3">
                  <IconButtonLink to={`/forms/${f.id}`} size="sm" icon="edit" aria-label="Apri" />
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </Page>
  )
}
